/**
 * ActionCard Component
 *
 * Renders clickable action cards in chat responses.
 * Used to open Settings modal to specific tabs or navigate to dashboard sections.
 * Similar to EntityCard but for actions rather than entities.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const ActionCard = ({ action, onOpenSettings, onNavigate }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();

    // Action types and their configurations (no emojis)
    const actionConfigs = {
        'open_webhook_settings': {
            label: t('chat.actions.openWebhookSettings', 'Open Webhook Settings'),
            color: '#e8f5e9',
            borderColor: '#81c784',
            textColor: '#2e7d32',
            actionType: 'settings',
            tab: 'webhook'
        },
        'open_threshold_settings': {
            label: t('chat.actions.openThresholdSettings', 'Open Threshold Settings'),
            color: '#fff3e0',
            borderColor: '#ffb74d',
            textColor: '#e65100',
            actionType: 'settings',
            tab: 'thresholds'
        },
        'open_language_settings': {
            label: t('chat.actions.openLanguageSettings', 'Open Language Settings'),
            color: '#e3f2fd',
            borderColor: '#64b5f6',
            textColor: '#1565c0',
            actionType: 'settings',
            tab: 'language'
        },
        'open_settings': {
            label: t('chat.actions.openSettings', 'Open Settings'),
            color: '#f3e5f5',
            borderColor: '#ba68c8',
            textColor: '#7b1fa2',
            actionType: 'settings',
            tab: 'thresholds'
        },
        'go_to_team_management': {
            label: t('chat.actions.goToTeamManagement', 'Go to Team Management'),
            color: '#e3f2fd',
            borderColor: '#64b5f6',
            textColor: '#1565c0',
            actionType: 'navigate',
            target: 'team_management'
        },
        'go_to_audit_log': {
            label: t('chat.actions.goToAuditLog', 'Go to Audit Log'),
            color: '#fce4ec',
            borderColor: '#f48fb1',
            textColor: '#c2185b',
            actionType: 'navigate',
            target: 'audit_log'
        }
    };

    const config = actionConfigs[action.type] || actionConfigs['open_settings'];

    const handleClick = () => {
        if (config.actionType === 'settings' && onOpenSettings) {
            onOpenSettings(config.tab);
        } else if (config.actionType === 'navigate' && onNavigate) {
            onNavigate(config.target);
        }
    };

    const cardStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 14px',
        margin: '4px',
        backgroundColor: config.color,
        border: `1px solid ${config.borderColor}`,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '13px',
        fontWeight: 500,
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)'
    };

    const labelStyle = {
        color: config.textColor,
        maxWidth: '220px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };

    const arrowStyle = {
        color: config.textColor,
        opacity: 0.7,
        marginLeft: '4px',
        transition: 'transform 0.2s ease',
        transform: isHovered ? 'translateX(2px)' : 'translateX(0)'
    };

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={cardStyle}
            title={config.label}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleClick();
                }
            }}
        >
            <span style={labelStyle}>{action.label || config.label}</span>
            <span style={arrowStyle}>&rarr;</span>
        </div>
    );
};

ActionCard.propTypes = {
    action: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string
    }).isRequired,
    onOpenSettings: PropTypes.func,
    onNavigate: PropTypes.func
};

ActionCard.defaultProps = {
    onOpenSettings: null,
    onNavigate: null
};

export default ActionCard;

